import { i18n } from '@/i18n'
import * as API from '@/modules/matrix/api/matrixApi'
import {
  IExposePartnerParams,
  IGetPendingBoostersParams,
  IInfinityPartnersParams,
  IPartners,
} from '@/modules/matrix/interfaces/partners.interface'
import type { Matrix } from '@/modules/matrix/interfaces/store.interface'
import {
  ActionContext,
  Commit,
} from 'vuex'
import { useMatrixSession } from '@/modules/matrix/bridges/useMatrixSession'
import { useMatrixNotify } from '@/modules/matrix/bridges/useMatrixNotify'
import { resolveStickyTab } from '@/modules/matrix/store/stickyTab'
import { isStaleMatrixType } from '@/modules/matrix/store/staleGuard'
import { isAbortError, takeAbortSignal } from '@/modules/matrix/store/requestAbort'
import type { MatrixStoreDependencies } from '../../api/matrixClient'

export function createPartners({ client: API, session: useMatrixSession, signal: takeAbortSignal }: MatrixStoreDependencies) {
return {
  namespaced: true,
  state() {
    return {
      partnersExposed: {} as IPartners,
      partnersPending: {} as IPartners,
      newPartnersPending: {} as IPartners,
      infinityPartners: null as Matrix[] | null,
      countPendingBoosters: null,
      levelID: 1 as number,

      pageIdPartners: 1,
      activeLittleTabID: null,

      // Матрица партнёра
      partnersPendingSecond: {} as IPartners,
      infinityPartnersSecond: [] as Matrix[],
    }
  },
  actions: {
    async getExposedPartners(
      { commit, rootState, state }: { commit: Commit; rootState: any; state: any },
      { filter, changeTab = false }: IGetPendingBoostersParams,
    ) {
      state.levelID = filter
      const matrixType = rootState.newTypeMatrix ? rootState.newTypeMatrix : rootState.selectedType?.type

      if (matrixType) {
        const matrixFilterUserId = (await useMatrixSession().ensureUserId()) ?? 0
        return API.filterOfActivatedMatrix({
          matrixType,
          matrixFilterPageId: state.pageIdPartners,
          matrixFilterUserId,
          filter: { level: filter },
        }, { signal: takeAbortSignal('partners:exposed') }).then(response => {
          // Быстрое листание уровней: ответ уже покинутого уровня не должен
          // перезаписать списки текущего (мигание «партнёров нет» ↔ список).
          if (isStaleMatrixType(rootState, matrixType)) return
          commit('SET_EXPOSED_PARTNERS', response.data)

          // «Липкая» саб-вкладка: остаёмся в текущей, если в ней есть элементы;
          // на соседнюю уходим, только когда текущая пуста, а соседняя — нет.
          if (changeTab && rootState.bigTabID === 1) {
            const pendingCount = state.newPartnersPending?.totalCount ?? 0
            const exposedCount = response.data?.totalCount ?? 0
            const current = rootState.littleTabID === 2 ? 2 : 1
            const tab = resolveStickyTab(current, { 1: pendingCount, 2: exposedCount })
            commit('CHANGE_LITTLE_TAB', tab, { root: true })
            commit('SET_ACTIVE_LITTLE_TAB', tab)
          }
        }).catch(error => {
          if (!isAbortError(error)) throw error // отменённый запрос — не ошибка
        })
      }
    },

    // Партнеры в ожидании для блока с табами
    async getNewPendingPartners(
      { commit, rootState, state }: { commit: Commit; rootState: any; state: any },
      { filter, changeTab = false }: IGetPendingBoostersParams,
    ) {
      state.levelID = filter
      const type = rootState.newTypeMatrix ? rootState.newTypeMatrix : rootState.selectedType?.type

      if (type) {
        const matrixFilterUserId = (await useMatrixSession().ensureUserId()) ?? 0
        return API.filterOfActivatedMatrix({
          matrixType: type,
          matrixFilterPageId: state.pageIdPartners,
          matrixFilterUserId,
          filter: { pending: 1, level: filter },
        }, { signal: takeAbortSignal('partners:pending') }).then(response => {
          // Быстрое листание уровней: устаревший ответ обновлять списки не должен
          if (isStaleMatrixType(rootState, type)) return
          if (state.pageIdPartners === 1 && filter === 1 && type === rootState.selectedType?.type) {
            commit('SET_PENDING_PARTNERS', response.data)
          }

          commit('SET_NEW_PENDING_PARTNERS', response.data)

          // «Липкая» саб-вкладка: остаёмся в текущей, если в ней есть элементы;
          // на соседнюю уходим, только когда текущая пуста, а соседняя — нет.
          if (changeTab && rootState.bigTabID === 1) {
            const pendingCount = response.data?.totalCount ?? 0
            const exposedCount = state.partnersExposed?.totalCount ?? 0
            const current = rootState.littleTabID === 2 ? 2 : 1
            const tab = resolveStickyTab(current, { 1: pendingCount, 2: exposedCount })
            commit('CHANGE_LITTLE_TAB', tab, { root: true })
            commit('SET_ACTIVE_LITTLE_TAB', tab)
          }
        }).catch(error => {
          if (!isAbortError(error)) throw error // отменённый запрос — не ошибка
        })
      }
    },
    async getPendingPartners(
      { commit, rootState, state }: { commit: Commit; rootState: any; state: any },
      {
        isPartnerMatrix = false,
        matrixType = rootState.selectedType?.type,
        matrixUUID,
        ownerID,
      }: IGetPendingBoostersParams,
    ) {
      if (matrixType) {
        const matrixFilterUserId = ownerID ?? (await useMatrixSession().ensureUserId()) ?? 0
        return API.filterOfActivatedMatrix({
          matrixType,
          matrixFilterPageId: state.pageIdPartners,
          matrixFilterUserId,
          filter: { pending: 1 },
          matrixUUID,
        }).then(response => {
          // Партнеры в ожидании Нашей матрицы
          if (!isPartnerMatrix) {
            commit('SET_PENDING_PARTNERS', response.data)
            // Партнеры в ожидании Матрицы партнера
          } else {
            commit('SET_PENDING_PARTNERS_SECOND', response.data)
          }
        })
      }
    },
    getInfinityPartners({ commit }: { commit: Commit }, { parentId, isPartnerMatrix = false }: IInfinityPartnersParams) {
      return API.getListOfInfinity(parentId).then(response => {
        if (!isPartnerMatrix) {
          commit('SET_INFINITY_PARTNERS', response.data.list)
        } else {
          commit('SET_INFINITY_PARTNERS_SECOND', response.data.list)
        }
      })
    },
    async exposePartner(ctx: ActionContext<any, any>, data: IExposePartnerParams) {
      const notificationText = data?.notificationText
      delete data.notificationText
      const response = await API.placementExistingMatrix(data)
      if (response?.error_code !== undefined) return response

      useMatrixNotify().success(notificationText ?? i18n.global.t('matrix.notify.partnerSent'))
      try {
        const refresh = ctx.dispatch('getMatrixByType', ctx.rootState.selectedType?.type, { root: true })
        void Promise.resolve(refresh).catch(() => undefined)
      } catch {
        // Placement is already committed; a refresh failure must not make it retryable.
      }

      return response
    },
  },
  mutations: {
    SET_EXPOSED_PARTNERS(state: any, partnersExposed: Object) {
      state.partnersExposed = partnersExposed
    },
    SET_PENDING_PARTNERS(state: any, partnersPending: Object) {
      state.partnersPending = partnersPending
    },
    SET_PENDING_PARTNERS_SECOND(state: any, partnersPending: Object) {
      state.partnersPendingSecond = partnersPending
    },
    SET_NEW_PENDING_PARTNERS(state: any, newPartnersPending: Object) {
      state.newPartnersPending = newPartnersPending
    },
    SET_INFINITY_PARTNERS(state: any, infinityPartners: Matrix[]) {
      state.infinityPartners = infinityPartners
    },
    SET_INFINITY_PARTNERS_SECOND(state: any, infinityPartners: Matrix[]) {
      state.infinityPartnersSecond = infinityPartners
    },
    SET_COUNT_PENDING_BOOSTERS(state: any, count: number) {
      state.countPendingBoosters = count
    },
    SET_PAGE_ID_PARTNERS(state: any, pageIdPartners: number) {
      state.pageIdPartners = pageIdPartners
    },
    SET_ACTIVE_LITTLE_TAB(state: any, id: number) {
      state.activeLittleTabID = id
    },
  },
  getters: {},
}
}

export default createPartners({ client: API, session: useMatrixSession, signal: takeAbortSignal })
